/**
 * How tall the feedback panel stands, per step.
 *
 * THE RULE: a step whose content is a form, a spinner or a one-line
 * confirmation sizes to its content; a step holding a list or a transcript
 * keeps a definite height. Both are capped at the same value, so nothing can
 * outgrow the viewport.
 *
 * WHY IT IS PER-STEP. Two opposite complaints have been filed about this panel:
 *
 *   2026-09-02  a fixed 400x520 box left "Ticket submitted!" floating in ~260px
 *               of empty space
 *   2026-09-08  a full-height panel (#639) leaves a short form in ~1448px of it
 *
 * The 2026-09-07 product decision that produced the second one chose the
 * definite height deliberately — long steps had been scrolling inside a 520px
 * box while most of the window sat empty — and recorded that if the empty-space
 * complaint returned, "the fix is per-STEP sizing, not reverting this line".
 * Reverting swaps one ticket for the other; this satisfies both.
 *
 * Geometry is unchanged: the panel is anchored `bottom-14` above a `bottom-6`
 * FAB, so 10rem spends the same 80px at the top and the cap lands just under
 * the 64px app header.
 */

export type FeedbackStep =
  | 'idle'
  | 'identity'
  | 'writing'
  | 'capturing'
  | 'summarizing'
  | 'confirming'
  | 'submitting'
  | 'done'

export type FeedbackView = 'chat' | 'my-tickets'

/**
 * Steps that hold a fixed, small amount of content.
 *
 * `confirming` is deliberately NOT here: it renders the whole drafted ticket
 * (summary, user story, acceptance criteria) and is one of the long steps the
 * definite height was introduced for.
 */
export const COMPACT_STEPS: readonly FeedbackStep[] = [
  'idle',
  'identity',
  'capturing',
  'submitting',
  'done',
]

/** The shared ceiling. Applied to every step, short or long. */
const CAP = 'max-h-[calc(100vh-10rem)]'
/** The definite height, for steps that scroll. */
const FULL = 'h-[calc(100vh-10rem)]'

/**
 * Tailwind height classes for the panel.
 *
 * The VIEW outranks the step: the ticket list is long regardless of which step
 * the chat happens to be parked on.
 */
export function feedbackPanelSizing(
  step: FeedbackStep,
  view: FeedbackView,
): string {
  const compact = view === 'chat' && COMPACT_STEPS.includes(step)
  return compact ? CAP : `${FULL} ${CAP}`
}
